import sounds from 'src/lib/sounds';
import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import BitmapFontTextView from '@play-co/timestep-core/lib/ui/bitmapFont/BitmapFontTextView';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts, { bitmapFontFromFontID } from 'src/lib/bitmapFonts';
import i18n, { loadableLocales } from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import StateObserver from 'src/StateObserver';
import { UserSettings } from 'src/replicant/State';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { createEmitter } from 'src/lib/Emitter';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import Checkbox from '../shared/Checkbox';
import { NotificationSettings } from 'src/replicant/chatbot/chatbot.ruleset';
import { getSupportMail, isPlatformGuest } from 'src/lib/utils';
import platform from '@play-co/gcinstant';
import { CheckboxGroup } from '../shared/CheckboxGroup';
import { startDeleteAccountSequence } from 'src/lib/account';
import { encodePacket } from 'src/replicant/utils/encoder';
import { isWebPlayable } from 'src/replicant/utils';

const getSkin = () =>
  ({
    root: 'assets',
    opts: {
      width: 591,
      height: 694 + 73,
    },
    line: {
      width: 497,
      height: 9,
      x: 30,
      y: 415 + 83,
    },
    userId: {
      height: 80,
      x: 40,
      y: 445 + 83,
      size: 31,
      color: '#f4aee4',
      font: bitmapFonts('Title'),
    },
    toggleSwitcher: {
      width: 105,
      height: 46,
      x: 460,
      y: 14,
      font: 'Body',
      fontSize: 20,
      labelPaddingX: 15,
    },
    supportButton: {
      ...uiConfig.buttons.primary,
      y: 487,
      labelOffsetY: -1,
      fontSize: 28,
      font: 'Body',
      height: 80,
      width: 360,
      centerOnOrigin: true,
    },
    guestLogout: {
      ...uiConfig.buttons.primary,
      y: 395,
      labelOffsetY: -1,
      fontSize: 28,
      font: 'Body',
      height: 80,
      width: 269,
      centerOnOrigin: true,
    },
    guestDeleteAccount: {
      ...uiConfig.buttons.primary,
      y: 395,
      labelOffsetY: -1,
      fontSize: 28,
      font: 'Body',
      height: 80,
      width: 269,
      centerOnOrigin: true,
    },
  } as const);

const languages = {
  en: 'English',
  ja: '日本語',
};

const languageData = Object.keys(loadableLocales).map((locale, index) => {
  return {
    index,
    key: locale,
    localeText: () => languages[locale],
  };
});

type Settings = UserSettings;
type SettingsKey = keyof Settings | 'notifications';

type DropdownStyleConfig = {
  width: number;
  height: number;
  x: number;
  y: number;
};

export default class PopupSettings extends PopupBasic {
  settings: Settings;
  languageDropdown: CheckboxGroup;
  notificationDropdown: CheckboxGroup;

  constructor(opts: { superview: View; close: () => void }) {
    const user = StateObserver.getState().user;

    const skin = getSkin();

    const sharedButtonHeight = skin.supportButton.height;

    super({
      ...opts,
      ...skin.opts,
      height: skin.opts.height,
      tracking: { feature: 'settings', $subFeature: 'popup-settings' },
      titleIcon: { image: 'assets/ui/buruburu/settings_icon.png' },
    });

    if (process.env.IS_DEVELOPMENT) {
      const appInfo = `v${process.env.APP_VERSION}\n(${process.env.GIT_BRANCH} / ${process.env.COMMITHASH})`;
      const label = new BitmapFontTextView({
        superview: this.box,
        x: 0,
        y: this.box.style.height + 50,
        width: this.box.style.width,
        align: 'center',
        verticalAlign: 'top',
        wordWrap: true,
        size: 30,
        font: bitmapFontFromFontID('Title'),
        color: 'white',
        text: appInfo,
      });
    }

    const gid = StateObserver.getState().user.id;
    const appName = capitalizeFirstLetterAll(process.env.APP_ID, '-');
    const appVersion = process.env.APP_VERSION;

    new BitmapFontTextView({
      superview: this.box,
      x: 0,
      y: this.box.style.height * 0.5 - 20,
      width: this.box.style.width,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
      size: 20,
      font: bitmapFontFromFontID('Body'),
      color: '#91808C',
      text: `GID: ${gid}\n${appName} v${appVersion}`,
    });

    const userSettings = user.settings;

    // Store all settings in replicant
    this.settings = {
      ...userSettings,
    };

    // Explicitly sort keys.
    const keys: SettingsKey[] = ['music', 'sound', 'notifications'];
    if (isPlatformGuest(platform)) {
      keys.splice(keys.indexOf('notifications'), 1);
    }

    // create items
    keys.forEach((key, i) => this.createSettingsItem(i, key));

    // Guest mode buttons are suitable for web version too, as the FAQ is mostly LINE related
    if (isPlatformGuest(platform) || isWebPlayable()) {
      this.createGuestModeButtons();
    } else {
      this.createFaqButton();
    }

    const linkUI = {
      x: this.box.style.width / 2,
      labelOffsetY: -1,
      fontSize: 28,
      font: 'Body',
      height: sharedButtonHeight,
      width: 300,
      centerOnOrigin: true,
      textColor: '#53AAF4',
    } as const;
    const underlineUI = {
      width: linkUI.width,
      y: (linkUI.height + linkUI.fontSize) / 2,
      height: 2,
      backgroundColor: linkUI.textColor,
      canHandleEvents: false,
    };

    // Line shows Privacy policy & Terms of Service buttons
    const privacyLink = new ButtonScaleViewWithText({
      ...linkUI,
      y: 487 + sharedButtonHeight,
      superview: this.box,
      localeText: () => i18n('platform.line.terms.pp.settingsBtn'),
      onClick: async () => {
        sounds.sfx.play('ok');
        //await openPopupPromise('popupTermsPolicyDetails', { mode: 'pp' });
        window.open('https://www.play.co/privacy');
      },
      tracking: { feature: 'settings', $subFeature: 'popup-settings-terms-pp' },
    });
    const privacyUnderline = privacyLink.addSubview(new View(underlineUI));

    const tosLink = new ButtonScaleViewWithText({
      ...linkUI,
      y: 487 + sharedButtonHeight + 50,
      superview: this.box,
      localeText: () => i18n('platform.line.terms.tos.settingsBtn'),
      onClick: async () => {
        sounds.sfx.play('ok');
        //await openPopupPromise('popupTermsPolicyDetails', { mode: 'tos' });
        window.open('https://www.play.co/tos');
      },
      tracking: {
        feature: 'settings',
        $subFeature: 'popup-settings-terms-tos',
      },
    });
    const tosUnderline = tosLink.addSubview(new View(underlineUI));

    createEmitter(this.box, ({ ui }) => ui.locale).addListener((locale) => {
      if (locale === 'en') {
        privacyUnderline.updateOpts({
          offsetX: 44,
          width: 216,
        });
        tosUnderline.updateOpts({
          offsetX: 29,
          width: 248,
        });
      } else {
        privacyUnderline.updateOpts({
          offsetX: 42,
          width: 225,
        });
        tosUnderline.updateOpts({
          offsetX: 96,
          width: 114,
        });
      }
    });

    this.buttonClose.removeFromSuperview();
    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...uiConfig.buttons.cancel,
        fontSize: 40,
        labelOffsetY: -2,
        font: 'Body',
        width: 235,
        height: 90,
        x: this.box.style.width / 2,
        y: this.box.style.height - 20 - 90 / 2,
        centerOnOrigin: true,
        localeText: () => i18n('basic.back'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close();
        },
        tracking: { feature: 'settings', $subFeature: 'popup-settings-back' },
      }),
    );
  }

  private createSettingsItem(i: number, key: SettingsKey) {
    const { width } = this.box.style;
    const item = new View({
      superview: this.box,
      x: 0,
      y: 103 + i * (73 + 10),
      width,
      height: 73,
    });

    item.addSubview(
      new LangBitmapFontTextView({
        x: 20,
        y: 15,
        align: 'left',
        verticalAlign: 'center',
        size: 32,
        color: '#BF8147',
        wordWrap: false,
        font: 'Body',
        centerOnOrigin: true,
        localeText: () => i18n(`settings.${key}`),
      }),
    );

    if (key === 'locale') {
      this.languageDropdown = this.createLanguageOptions(item, {
        x: 272,
        y: 12,
        width: 335,
        height: 46,
      });
    } else if (key === 'notifications') {
      this.notificationDropdown = this.createNotificationOptions(item, {
        x: 332,
        y: 14,
        width: 335,
        height: 46,
      });
    } else {
      this.createSwitcher(item, key);
    }

    // separator
    item.addSubview(
      new View({
        height: 5,
        width: width - 12,
        x: 6,
        y: item.style.height - 5,
        backgroundColor: '#FDC381',
      }),
    );
  }

  private createGuestModeButtons() {
    const { guestLogout, guestDeleteAccount, supportButton } = getSkin();

    /*
    // Not sure if that should be added as the login happens automatically when user opens the game
    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...guestLogout,
        x: this.box.style.width * 0.26,
        localeText: () => i18n('guest.logout'),
        onClick: async () => {
          sounds.sfx.play('ok');
          window.Native?.login?.logout();
        },
        tracking: {
          feature: 'guest',
          $subFeature: 'guest_logout',
        },
      }),
    );

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...guestDeleteAccount,
        x: this.box.style.width * 0.74,
        localeText: () => i18n('deleteAccount.action'),
        onClick: async () => {
          sounds.sfx.play('ok');
          await startDeleteAccountSequence();
        },
        tracking: {
          feature: 'guest',
          $subFeature: 'guest_delete_account',
        },
      }),
    );
    */

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...supportButton,
        x: this.box.style.width * 0.5,
        localeText: () => i18n('error.support.contactSupport'),
        onClick: async () => {
          sounds.sfx.play('ok');
          const supportEmail = getSupportMail();
          const subject = `${i18n('error.support.supportSubject')} GID:${
            StateObserver.getState().user.id
          }`;
          window.open(`mailto:${supportEmail}?subject=${subject}`);
        },
        tracking: {
          feature: 'settings',
          $subFeature: 'popup-settings-contact-support',
        },
      }),
    );
  }

  private createFaqButton() {
    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...getSkin().supportButton,
        x: this.box.style.width / 2,
        localeText: () => i18n('settings.faq'),
        onClick: async () => {
          sounds.sfx.play('ok');
          await openPopupPromise('popupFaq', {});
        },
        tracking: { feature: 'settings', $subFeature: 'popup-settings-faq' },
      }),
    );
  }

  init(opts: {}) {
    super.init(opts);
    this.title.setText(() => i18n('settings.title').toUpperCase());
  }

  toggleSwitcher(switcher: ButtonScaleViewWithText, key: string) {
    // save to platform.storage
    this.settings[key] = !this.settings[key];
    this.updateSwitcher(switcher, key, this.settings[key]);
  }

  updateSwitcher(
    switcher: ButtonScaleViewWithText,
    key: string,
    value: boolean,
  ) {
    const skin = getSkin();
    const url = value
      ? `${skin.root}/ui/shared/lossless/btn_toggle_on.png`
      : `${skin.root}/ui/shared/lossless/btn_toggle_off.png`;
    const labelOpts: LangBitmapOpts = value
      ? { color: '#FFFFFF', align: 'left' }
      : { color: '#A04C33', align: 'right' };
    labelOpts.localeText = () => i18n(`settings.${value ? 'on' : 'off'}`);

    switcher.updateButtonImage(url);
    switcher.label.updateOpts(labelOpts);

    switch (key) {
      case 'music':
        sounds.musicMuted = !value;
        break;
      case 'sound':
        sounds.sfxMuted = !value;
        break;
      default:
        break;
    }

    void StateObserver.invoke.updateSettings({
      [key]: value,
    });
  }

  createSwitcher(item: View, key: string) {
    const skin = getSkin();
    const switcher = new ButtonScaleViewWithText({
      ...skin.toggleSwitcher,
      superview: item,
      onClick: async () => {
        sounds.sfx.play('ok');
        return this.toggleSwitcher(switcher, key);
      },
      tracking: { feature: 'settings', $subFeature: `popup-settings-${key}` },
    });

    this.updateSwitcher(switcher, key, this.settings[key]);

    return switcher;
  }

  private createNotificationOptions(parent: View, config: DropdownStyleConfig) {
    const container = new View({
      ...config,
      superview: parent,
    });

    // get item index to pre-select
    const { receiveMessages } = StateObserver.getState().user.settings;
    const checkboxes = Object.keys(NotificationSettings)
      .filter((f) => f !== 'Less')
      .map((settings, index) => {
        const checkbox = new Checkbox({
          superview: container,
          x: index * 100 + 70,
          y: -25,
          checked: NotificationSettings[settings] === receiveMessages,
        });

        container.addSubview(
          new ButtonScaleViewWithText({
            x: index * 100 + 40,
            y: checkbox.view.style.height - 35,
            align: 'center',
            verticalAlign: 'center',
            fontSize: 26,
            width: 100,
            height: 46,
            labelHorizontalAlign: 'center',
            labelPaddingX: 0,
            textColor: '#BF8147',
            wordWrap: false,
            font: 'Body',
            localeText: () => i18n(`settings.${settings.toLowerCase()}`),
            onClick: async () => checkbox.toggle(),
            tracking: {
              feature: 'settings',
              $subFeature: `popup-settings-receiveMessages-${settings}`,
            },
          }),
        );

        return {
          id: settings,
          checkbox,
        };
      });

    return new CheckboxGroup({
      options: checkboxes,
      onSelect: (id: string) => {
        sounds.sfx.play('ok');
        void StateObserver.invoke.updateSettings({
          receiveMessages: NotificationSettings[id],
        });
      },
    });
  }

  private createLanguageOptions(parent: View, config: DropdownStyleConfig) {
    const container = new View({
      ...config,
      superview: parent,
    });

    // get item index to pre-select
    const { locale } = StateObserver.getState().user.settings;
    const checkboxes = languageData.map((language, index) => {
      const checkbox = new Checkbox({
        superview: container,
        x: index * 170,
        checked: language.key === locale,
      });

      container.addSubview(
        new ButtonScaleViewWithText({
          x: index * 170 + 40,
          align: 'left',
          verticalAlign: 'center',
          fontSize: 26,
          width: 118,
          height: 46,
          labelHorizontalAlign: 'left',
          labelPaddingX: 0,
          textColor: '#BF8147',
          wordWrap: false,
          font: 'Body',
          localeText: language.localeText,
          onClick: async () => checkbox.toggle(),
          tracking: {
            feature: 'settings',
            $subFeature: `popup-settings-language-${language.key}`,
          },
        }),
      );

      return {
        id: language.key,
        checkbox,
      };
    });

    return new CheckboxGroup({
      options: checkboxes,
      onSelect: (locale: string) => {
        sounds.sfx.play('ok');
        void StateObserver.invoke.updateSettings({ locale });
      },
    });
  }
}

function capitalizeFirstLetter(str: string) {
  str = str.toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function capitalizeFirstLetterAll(str: string, separator = ' ') {
  return str.split(separator).map(capitalizeFirstLetter).join(separator);
}
